"use client"

import { usePathname } from "next/navigation"
import { ReactNode, useEffect, useState } from "react"
import Image from "next/image"
import Anchor from "@shared-ui/components/Anchor"
import { T, Var, useGT } from "gt-next"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { classNames } from "@/lib/utils"
import { motion, AnimatePresence } from "framer-motion"
import { AvatarGroup, AvatarGroupItem, AvatarGroupTooltip, AvatarGroupYou, SimpleAvatar } from "@/components/ui/avatar-group"
import { FlickeringGrid } from "@/components/ui/flickering-grid"
import LandingLogosCarousel from "@/components/landing/LandingLogosCarousel"
import { getFeaturedClients } from "@/utils/clients"

// Curated testimonials for auth pages from centralized clients system
const AUTH_TESTIMONIALS = getFeaturedClients()
    .slice(0, 4)
    .map((c) => ({
        id: c.id,
        quote: c.testimonial?.quote || "",
        name: c.name,
        role: c.niche.charAt(0).toUpperCase() + c.niche.slice(1) + " Creator",
        avatarUrl: c.avatar,
        growth: c.metrics
            ? `+${Math.round((c.metrics.followersAfter - c.metrics.followersBefore) / 1000)}K followers`
            : "+10K followers",
        growthK: c.metrics ? Math.round((c.metrics.followersAfter - c.metrics.followersBefore) / 1000) : null
    }))

// Avatar data for the group (using same data)
const AUTH_AVATARS = AUTH_TESTIMONIALS.map((t) => ({
    src: t.avatarUrl,
    name: t.name.split(" ")[0] + " " + (t.name.split(" ")[1]?.[0] || "") + ".",
    fallback: t.name.split(" ").map(w => w[0]).join("").toUpperCase(),
    growth: t.growth, // e.g. "+119K followers"
    growthK: t.growthK
}))

// Translatable copies of the client testimonial data (src/utils/clients.ts stays unchanged).
// Keys are the exact English strings; unknown values fall back to the raw English.
const QUOTE_T: Record<string, ReactNode> = {
    "Went from 8k to 127k in 6 months. My brand deals tripled.": <T>Went from 8k to 127k in 6 months. My brand deals tripled.</T>,
    "156K followers and getting paid to travel. This changed my life.": <T>156K followers and getting paid to travel. This changed my life.</T>,
    "My art is finally reaching people who actually appreciate it.": <T>My art is finally reaching people who actually appreciate it.</T>,
    "6.8K to 92K in under 7 months. Makeup brands now reach out to me.": <T>6.8K to 92K in under 7 months. Makeup brands now reach out to me.</T>
}
const ROLE_T: Record<string, ReactNode> = {
    "Fashion Creator": <T>Fashion Creator</T>,
    "Travel Creator": <T>Travel Creator</T>,
    "Art Creator": <T>Art Creator</T>,
    "Beauty Creator": <T>Beauty Creator</T>
}

function GrowthLabel({ growth, growthK }: { growth: string; growthK: number | null }) {
    if (growthK !== null) return <T>+<Var name="count">{growthK}</Var>K followers</T>
    if (growth === "+10K followers") return <T>+10K followers</T>
    return <>{growth}</>
}

export function LoginPagesClientLayout({ children }: { children: ReactNode }) {
    const queryClient = new QueryClient()
    const pathname = usePathname()
    const isLoggedInSetup = pathname.startsWith("/app/setup")

    // State for managing testimonials
    const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0)

    // Auto-rotate testimonials every 6 seconds
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTestimonialIndex((prev) => (prev + 1) % AUTH_TESTIMONIALS.length)
        }, 6000)

        return () => clearInterval(timer)
    }, [])

    const currentTestimonial = AUTH_TESTIMONIALS[currentTestimonialIndex]
    const gt = useGT()

    return (
        <QueryClientProvider client={queryClient}>
            <div className="min-h-screen lg:flex">
                {/* Left side - Form */}
                <div className={classNames("relative flex min-h-screen flex-col bg-white px-6 py-8 sm:px-8 lg:min-h-full lg:flex-1 lg:px-16 xl:px-20", !isLoggedInSetup && "justify-center")}>
                    {/* Logo - Fixed at top-left on desktop, centered on mobile */}
                    <div className="mb-8 lg:absolute lg:left-16 lg:top-8 lg:mb-0 xl:left-20">
                        <Anchor href="/" className="block">
                            <Image src="/img/outfame-logo-full.svg" alt="Outfame" width={140} height={32} className="mx-auto h-7 w-auto lg:mx-0" priority />
                        </Anchor>
                    </div>

                    {/* Form container - premium narrow width */}
                    <div className="mx-auto w-full max-w-[340px] lg:max-w-[380px]">{children}</div>
                </div>

                {/* Right side - Social Proof Panel with inset rounded card */}
                <div className="relative hidden w-full bg-white p-3 lg:flex lg:max-w-[580px] lg:flex-1 xl:p-4">
                    {/* Inner pink card with rounded corners */}
                    <div className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-[24px] px-10 xl:px-14" style={{ backgroundColor: "#ffa3d1" }}>
                        {/* Flickering grid background - subtle white dots for elegant texture */}
                        <FlickeringGrid
                            className="absolute inset-0"
                            squareSize={3}
                            gridGap={24}
                            color="#ffffff"
                            maxOpacity={0.35}
                            flickerChance={0.04}
                            squareRound={1.5}
                        />

                        {/* Content wrapper */}
                        <div className="relative z-10 flex max-w-md flex-col items-center">
                        {/* Testimonial card with AnimatePresence - THE hero element */}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentTestimonial.id}
                                initial={{ opacity: 0, y: 20, scale: 0.98 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -20, scale: 0.98 }}
                                transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                            className="w-full rounded-[20px] bg-white p-6 shadow-2xl"
                        >
                            {/* Quote - prominent, attention-grabbing */}
                            <p className="mb-5 text-[17px] font-medium leading-[1.6] tracking-[-0.01em] text-ink">"{QUOTE_T[currentTestimonial.quote] ?? currentTestimonial.quote}"</p>

                            {/* Author */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Image src={currentTestimonial.avatarUrl} alt={currentTestimonial.name} width={44} height={44} className="rounded-full object-cover ring-2 ring-ink/10" />
                                    <div>
                                        <p className="text-[14px] font-semibold text-ink">{currentTestimonial.name}</p>
                                        <p className="text-[12px] text-ink/50">{ROLE_T[currentTestimonial.role] ?? currentTestimonial.role}</p>
                                    </div>
                                </div>

                                {/* Growth badge with icon */}
                                <div className="flex items-center gap-1 rounded-lg bg-emerald-50 px-3 py-1.5">
                                    <svg className="h-3.5 w-3.5 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-[13px] font-bold text-emerald-600"><GrowthLabel growth={currentTestimonial.growth} growthK={currentTestimonial.growthK} /></span>
                                </div>
                            </div>
                        </motion.div>
                        </AnimatePresence>

                        {/* Dot indicators - dark on pink */}
                        <div className="mt-6 flex items-center gap-2">
                            {AUTH_TESTIMONIALS.map((_, index) => (
                                <button key={index} onClick={() => setCurrentTestimonialIndex(index)} className={`h-2 rounded-full transition-all duration-300 ${index === currentTestimonialIndex ? "w-6 bg-ink" : "w-2 bg-ink/25 hover:bg-ink/40"}`} aria-label={`View testimonial ${index + 1}`} />
                            ))}
                        </div>

                        {/* Avatar group with trust stat */}
                        <div className="mt-10 flex flex-col items-center">
                            <AvatarGroup className="justify-center" translate="-20%" side="top" sideOffset={8}>
                                {AUTH_AVATARS.map((avatar, index) => (
                                    <AvatarGroupItem key={index}>
                                        <SimpleAvatar src={avatar.src} alt={avatar.name} fallback={avatar.fallback} size="md" className="ring-white shadow-lg" />
                                        <AvatarGroupTooltip>
                                            <p className="font-medium">{avatar.name}</p>
                                            <p className="text-[14px] text-emerald-400"><GrowthLabel growth={avatar.growth} growthK={avatar.growthK} /></p>
                                        </AvatarGroupTooltip>
                                    </AvatarGroupItem>
                                ))}
                                <AvatarGroupYou size="md" tooltipText={gt("You could be next!")} href="/sign-up" variant="light" />
                            </AvatarGroup>
                            <p className="mt-3 text-center text-[13px] font-medium text-ink/60">
                                <T>Join <span className="font-semibold text-ink/80">65,000+</span> creators & brands</T>
                            </p>
                        </div>

                        {/* Google reviews badge - solid white, the only badge element */}
                        <Anchor href="/outfame-reviews" className="mt-6 flex items-center gap-2 rounded-full bg-white px-4 py-2.5 shadow-md transition-all hover:shadow-lg">
                            {/* Google icon */}
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src="https://fonts.gstatic.com/s/i/productlogos/googleg/v6/24px.svg" alt="Google" width={18} height={18} className="h-[18px] w-[18px]" />
                            <span className="text-[15px] font-bold text-ink">4.9</span>
                            {/* 5 stars */}
                            <div className="flex gap-0.5">
                                {[...Array(5)].map((_, i) => (
                                    <svg key={i} className="h-4 w-4 fill-yellow-400" viewBox="0 0 20 20">
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                ))}
                            </div>
                            <span className="text-[13px] text-ink/60">
                                <T>from <span className="font-semibold text-ink/80">2,140+</span> reviews</T>
                            </span>
                        </Anchor>
                        {/* Opens in a new tab so the sign-up / setup flow stays where it is */}
                        <a href="/blog/is-outfame-legit" target="_blank" rel="noopener" className="mt-3 text-[13px] font-medium text-ink/60 underline decoration-ink/20 underline-offset-4 transition-colors hover:text-ink">
                            <T>Is <span translate="no">Outfame</span> legit? See the facts</T>
                        </a>
                        </div>

                        {/* Logo carousel at bottom */}
                        <div className="absolute inset-x-0 bottom-8">
                            <LandingLogosCarousel isAuthPage={true} />
                        </div>
                    </div>
                </div>
            </div>
        </QueryClientProvider>
    )
}
